import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import {
	ClientPropertyCategoryEnum,
	type ClientPropertyLandDetailResponseDTO,
	ClientPropertyOwnershipEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyDataCard } from "web_myplann/Portfolio/Components/PortfolioTools/PropertyDataCard";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import {
	DataCard,
	DataCardPreset,
} from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	dateFormatter,
	unitFormatter,
} from "web_myplann/Shared/Utils/formatters";

const PageResources = Resources.PortfolioDetail;

type Props = {
	data: ClientPropertyLandDetailResponseDTO;
	currencySign: string;
	documentsId: number;
};

export const Land: FC<Props> = ({ data, currencySign, documentsId }) => {
	const { t } = useResource();

	const { data: documents } = usePortfolioDocumentsQuery(documentsId);

	const ownership =
		data?.ownership && data.ownership !== ClientPropertyOwnershipEnum.None
			? t(PageResources.Property.OwnershipValue[data.ownership])
			: "";

	const landType =
		data.propertyCategory &&
		data.propertyCategory !== ClientPropertyCategoryEnum.None
			? t(PageResources.Property.LandTypeEnum[data.propertyCategory])
			: "";

	const bottomComponent =
		data.purchaseDate || data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.PurchaseDate)}
					value={dateFormatter(data.purchaseDate)}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description ?? ""}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
			>
				<Stack>
					<DataRow
						label={t(PageResources.Property.LandType)}
						value={landType}
					/>
					<DataRow
						label={t(PageResources.Property.StreetAndNumber)}
						value={data.location}
					/>
					<DataRow
						label={t(PageResources.Property.Municipality)}
						value={data.locationMunicipality}
					/>
					<DataRow
						label={t(PageResources.Property.ZipCode)}
						value={data.locationZipCode}
					/>
				</Stack>
			</DataCard>

			<PropertyDataCard
				reproductionValue={null}
				interiorPrice={data.reproductionValue}
				marketValue={data.landValue}
				priceHistory={data.estimatedPriceHistoryList}
				currencySign={currencySign}
				type={ClientPropertyTypeEnum.Land}
			/>

			<DataCard preset={DataCardPreset.PropertyParameters}>
				<Stack>
					<DataRow
						label={t(PageResources.Property.LandArea)}
						value={unitFormatter(data.landSize)}
					/>
					<DataRow
						label={t(PageResources.Property.Ownership)}
						value={ownership}
					/>
				</Stack>
			</DataCard>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
